<template>
    <div class="offer-selector"
         :class="{'offer-selector--has-supervisor-permission': hasApplySupervisorOfferPermission}">
        <template v-if="hasApplyOffersPermission">
            <slot></slot>

            <!-- Offer search box -->
            <div class="input-group input-group-lg mb-1">
                <span class="input-group-addon"><i class="fa-solid fa-magnifying-glass"></i></span>
                <input v-model="searchQuery"
                       type="search"
                       class="form-control i"
                       :placeholder="searchPlaceholder" />
            </div>

            <!-- Offers list -->
            <div v-if="filteredOffers && filteredOffers.length > 0"
                 class="btn-grid selectable-btns offer-selector__offers">
                <OfferButton v-for="offer in filteredOffers"
                             :key="offer.ID"
                             :offer="offer"
                             :selected="selectedOfferIDs.includes(offer.ID)"
                             :selected-count="selectedOffers[offer.ID]"
                             :disabled="disabledOfferIds.includes(offer.ID)"
                             :variable-discounts-collection="variableDiscountsCollection"
                             @select="selectOffer"
                             @deselect="deselectOffer" />
            </div>
            <div v-else
                 class="alert alert-info">
                {{ l('CART_OFFERS.NO_OFFERS') }}
            </div>
        </template>
        <!-- Strict type check because null means the permission is still being checked. -->
        <template v-else-if="hasApplyOffersPermission === false">
            <div class="alert alert-danger">
                {{ l('CART_OFFERS.NO_PERMISSION') }}
            </div>
        </template>
        <PageLoadingSpinner v-else />
    </div>
</template>

<script>
import OfferButton from '@/epos/vue/modals/CartOffers/OfferButton';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import VariableDiscount from '@/models/VariableDiscount';
import VariableDiscountsCollection from '@/models/VariableDiscountsCollection';
import { cloneDeep } from '@/functions/clone';
import { compareStringsForFilter, normalizeSearchQuery } from '@/functions/strings';
import { container } from '@/container';
import { langFilter } from '@/vue/filters/langFilter';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { sortByNavOrder } from '@/functions/arraySorting';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    name: 'OfferSelector',

    components: { OfferButton, PageLoadingSpinner },

    props: {
        offers: {
            type: Array,
            default: null,
        },

        /**
         * The offer(s) that the operator has selected.
         *
         * The keys will be offerIDs.
         * Some offers can be applied more than once depending on how many time it is clicked.
         * In that case the value will be a number >= 1, otherwise the value will be 1.
         *
         * @type {Object<number, number>}
         */
        initialSelectedOffers: {
            type: Object,
            required: true,
        },

        /**
         * Any offers where their ID appears in this array will be displayed to the user,
         * but as disabled buttons so they can't be selected.
         *
         * @type {number[]}
         */
        disabledOfferIds: {
            type: Array,
            default: () => [],
        },

        allowMultiple: {
            type: Boolean,
            default: false,
        },

        /**
         * For handling line-level variable discounts a VariableDiscountsCollection instance
         * should be passed in.
         *
         * @type {VariableDiscountsCollection}
         */
        variableDiscountsCollection: {
            type: Object,
            default: () => new VariableDiscountsCollection(),
        },
    },

    emits: [
        'update:selectedOffers',
    ],

    data() {
        return {
            /**
             * Does the current user has permission to apply offers?
             * We won't show anything if they don't.
             */
            hasApplyOffersPermission: null,

            /**
             * Does the current user has permission to apply supervisor-only offers?
             * We will show those offers but greyed out if they don't.
             */
            hasApplySupervisorOfferPermission: null,

            searchQuery: '',

            /**
             * This is a clone of the initialSelectedOffers passed in, so we can modify it.
             * Any changes to this should be emitted as update:selectedOffers.
             * See props.initialSelectedOffers.
             *
             * @type {Object<number, number>}
             */
            selectedOffers: Object.assign({}, this.initialSelectedOffers),
        };
    },

    computed: {
        searchPlaceholder() {
            return langFilter('CART_OFFERS.SEARCH');
        },

        sortedOffers() {
            if (!this.offers) {
                return [];
            }

            return sortByNavOrder(cloneDeep(this.offers));
        },

        filteredOffers() {
            if (!this.searchQuery) {
                return this.sortedOffers;
            }

            const normalizedSearchQuery = normalizeSearchQuery(this.searchQuery);

            return this.sortedOffers.filter((o) => compareStringsForFilter(o.name, normalizedSearchQuery));
        },

        selectedOfferIDs() {
            return Object.keys(this.selectedOffers).map((id) => parseInt(id));
        },
    },

    created() {
        container.privilegesManager
            .requirePrivilege(PrivilegeNames.APPLY_DISCOUNTS)
            .then((result) => {
                if (result.granted) {
                    this.hasApplyOffersPermission = true;

                    // Check if the user that granted the apply offers permission also has
                    // the apply_supervisor_discounts permission.
                    // Note that `grantedBy` could also be the current user if that user already had the permission.
                    container.privilegesManager
                        .checkUserPrivilege(
                            result.grantedBy,
                            PrivilegeNames.APPLY_SUPERVISOR_DISCOUNTS
                        )
                        .then(() => {
                            this.hasApplySupervisorOfferPermission = true;
                        })
                        .catch(() => {
                            this.hasApplySupervisorOfferPermission = false;
                        });
                }
            })
            .catch((e) => {
                if (e) {
                    console.warn(e);
                }
                this.hasApplyOffersPermission = false;
            });
    },

    methods: {
        /**
         * @param {Offer} offer
         */
        async selectOffer(offer) {
            if (offer.requiresSupervisorApproval) {
                try {
                    await this.checkForSupervisorOffersPermission();
                } catch (e) {
                    console.error('User does not have permission to apply supervisor offers.');
                    return;
                }
            }

            if (offer.isVariable()) {
                // If a variable discount is entered capture the amount for the offer.
                // Variable discounts are only supported on applied-to-line offers, so there should be no
                // multiple selection of variable discounts allowed.

                // If a variable discount has been selected we need to capture additional data
                // using the "variable discount modal".
                // To remove a variable discount use the 'Remove discount' button in the modal.

                // Either get the existing data from the collection or create a new VariableDiscount model.
                let variableDiscount = this.variableDiscountsCollection.getVariableDiscount(offer.ID);
                if (!variableDiscount) {
                    // No existing data for this discount. Create a new modal to pass to the modal.
                    variableDiscount = new VariableDiscount(offer.ID);
                }

                /**
                 * Send the discount model to the VariableDiscountModal.
                 *
                 * @type {VariableDiscount|null|undefined} VariableDiscount if the user pressed OK.
                 *                                         null if the user pressed the remove button.
                 *                                         undefined if the user cancelled the modal.
                 */
                const newVariableDiscount = await this.captureVariableOfferData(variableDiscount);
                if (newVariableDiscount) {
                    // Add the discount to the collection.
                    this.variableDiscountsCollection.addVariableDiscount(newVariableDiscount);
                } else {
                    console.log('Variable discount modal was cancelled.');
                    // The Variable Discount modal was cancelled so do nothing.
                    return;
                }
            }

            if (!this.allowMultiple) {
                // If selecting multiple offers is not allowed, remove all other selected offers
                // when selecting a new one. This will also clear any variable discount data for other offers.
                Object.keys(this.selectedOffers)
                    .map(parseInt)
                    .filter((offerID) => offerID !== offer.ID)
                    .forEach((offerID) => this.deselectOfferByID(offerID));
            }

            if (offer.applyPerTriggerLimit && this.selectedOfferIDs.includes(offer.ID)) {
                // If the offer supports being selected multiple times add another selection.
                ++this.selectedOffers[offer.ID];
            } else {
                // Otherwise select the offer 1 time.
                this.selectedOffers[offer.ID] = 1;
            }

            this.$emit('update:selectedOffers', this.selectedOffers);
        },

        /**
         * @param {Offer} offer
         */
        async deselectOffer(offer) {
            // Allow de-selecting supervisor offers without permission.
            // if (offer.requiresSupervisorApproval) {
            //     await this.checkForSupervisorOffersPermission();
            // }

            delete this.selectedOffers[offer.ID];
            this.variableDiscountsCollection.deleteVariableDiscount(offer.ID);
            this.$emit('update:selectedOffers', this.selectedOffers);
        },

        /**
         * @param {number} offerID
         */
        async deselectOfferByID(offerID) {
            const offer = this.offers.find((o) => o.ID === offerID);
            if (!offer) {
                return Promise.reject();
            }

            return this.deselectOffer(offer);
        },

        /**
         * @return {Promise<boolean>}
         */
        async checkForSupervisorOffersPermission() {
            if (this.hasApplySupervisorOfferPermission) {
                return true;
            }

            return container.privilegesManager
                .requirePrivilege(PrivilegeNames.APPLY_SUPERVISOR_DISCOUNTS)
                .then((result) => {
                    if (result.granted) {
                        this.hasApplySupervisorOfferPermission = true;
                    }
                    return true;
                });
        },

        /**
         * Open the modal to capture the amount and narrative for a variable discount.
         *
         * @param {VariableDiscount} variableDiscount
         * @param {{}} [templateText] (allows for modal customisation)
         * @param {boolean} [showSignButton] (not currently implemented)
         *
         * @returns {Promise<VariableDiscount|null>}
         */
        async captureVariableOfferData(
            variableDiscount,
            templateText,
            showSignButton,
        ) {
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.VARIABLE_DISCOUNT,
                    {
                        variableDiscount,
                        templateText,
                        showSignButton,
                    },
                )
                .then((result) => {
                    return result.variableDiscount || null;
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },
    },
};
</script>
